import { callable } from '../callable';

export type ModuleItemType = 'material' | 'page' | 'assignment';
export type ModuleItemTargetCollection = 'courseMaterials' | 'pages' | 'assignments';

export interface StructureItemDto {
  id: string;
  moduleId: string;
  type: ModuleItemType;
  targetCollection: ModuleItemTargetCollection;
  targetId: string;
  sortOrder: number;
  indent: 0 | 1 | 2;
  publishedAt?: string;
  title: string;
  material?: {
    materialType: 'video' | 'notes' | 'reading';
    description?: string;
    url?: string;
    externalSource?: string;
    storagePath?: string;
    mimeType?: string;
    sizeBytes?: number;
    originalFileName?: string;
  };
  page?: { slug: string };
  assignment?: {
    assignmentType: 'reading-review' | 'essay' | 'quiz' | 'project' | 'attendance' | 'other';
    dueAt?: string;
    lockAt?: string;
    pointsPossible: number;
    submissionTypes: ('file' | 'text' | 'url')[];
    isQuiz: boolean;
    published: boolean;
    submissionStatus?: 'none' | 'draft' | 'submitted' | 'graded' | 'returned';
  };
}

export interface StructureModuleDto {
  id: string;
  title: string;
  description?: string;
  sortOrder: number;
  publishedAt?: string;
  releaseAt?: string;
  /** FK to the syllabus template module this module came from; absent for manual modules. */
  templateModuleId?: string;
  /** YYYY-MM-DD of the module's first scheduled class, when it has any. */
  firstSessionDate?: string;
  /** YYYY-MM-DD of its last class — same as the first for a single-day module. */
  lastSessionDate?: string;
  items: StructureItemDto[];
}

export interface SectionStructureDto {
  sectionId: string;
  modules: StructureModuleDto[];
  unassignedItems: StructureItemDto[];
}

export interface PageDto {
  id: string;
  title: string;
  slug: string;
  bodyJson: Record<string, unknown>;
  version: number;
  sectionId: string;
  publishedAt?: string;
}

export const lmsApi = {
  // Section structure aggregate
  getSectionStructure: async (data: {
    tenantId: string;
    sectionId: string;
    requestingStudentId?: string;
  }): Promise<SectionStructureDto> => {
    const fn = callable<typeof data, SectionStructureDto>('getSectionStructure');
    const res = await fn(data);
    return res.data;
  },

  // Modules
  createModule: async (data: {
    tenantId: string;
    sectionId: string;
    title: string;
    description?: string;
    releaseAt?: string;
  }): Promise<{ id: string }> => {
    const fn = callable<typeof data, { id: string }>('createLmsModule');
    return (await fn(data)).data;
  },
  updateModule: async (data: {
    tenantId: string;
    moduleId: string;
    title?: string;
    description?: string;
    sortOrder?: number;
    releaseAt?: string | null;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('updateLmsModule');
    return (await fn(data)).data;
  },
  publishModule: async (data: {
    tenantId: string;
    moduleId: string;
    publish: boolean;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('publishLmsModule');
    return (await fn(data)).data;
  },
  deleteModule: async (data: {
    tenantId: string;
    moduleId: string;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('deleteLmsModule');
    return (await fn(data)).data;
  },
  reorderModules: async (data: {
    tenantId: string;
    sectionId: string;
    orderedIds: string[];
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('reorderLmsModules');
    return (await fn(data)).data;
  },

  // Module items
  createModuleItem: async (data: {
    tenantId: string;
    sectionId: string;
    moduleId: string;
    type: ModuleItemType;
    targetId: string;
    title?: string;
    indent?: 0 | 1 | 2;
  }): Promise<{ id: string }> => {
    const fn = callable<typeof data, { id: string }>('createModuleItem');
    return (await fn(data)).data;
  },
  updateModuleItem: async (data: {
    tenantId: string;
    itemId: string;
    title?: string;
    sortOrder?: number;
    indent?: 0 | 1 | 2;
    moduleId?: string;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('updateModuleItem');
    return (await fn(data)).data;
  },
  deleteModuleItem: async (data: {
    tenantId: string;
    itemId: string;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('deleteModuleItem');
    return (await fn(data)).data;
  },
  reorderModuleItems: async (data: {
    tenantId: string;
    moduleId: string;
    orderedIds: string[];
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('reorderModuleItems');
    return (await fn(data)).data;
  },

  // Pages
  createPage: async (data: {
    tenantId: string;
    sectionId: string;
    title: string;
    bodyJson?: Record<string, unknown>;
  }): Promise<{ id: string; slug: string }> => {
    const fn = callable<typeof data, { id: string; slug: string }>('createPage');
    return (await fn(data)).data;
  },
  updatePage: async (data: {
    tenantId: string;
    pageId: string;
    title?: string;
    bodyJson?: Record<string, unknown>;
  }): Promise<{ version: number }> => {
    const fn = callable<typeof data, { version: number }>('updatePage');
    return (await fn(data)).data;
  },
  deletePage: async (data: { tenantId: string; pageId: string }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('deletePage');
    return (await fn(data)).data;
  },
  getPage: async (data: {
    tenantId: string;
    pageId: string;
    requestingStudentId?: string;
  }): Promise<PageDto> => {
    const fn = callable<typeof data, PageDto>('getPage');
    return (await fn(data)).data;
  },
};
